import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { H1, ButtonV2, P, Span, Spinner } from 'shared';
import { usePostServicingV1AccountsLenderAccountIdentifierBankVerificationMutation, usePutServicingV1AccountsLenderAccountIdentifierBankVerificationExchangePublicTokenMutation, usePostServicingV1AccountsLenderAccountIdentifierBankAccountsMutation, } from 'servicing-api';
import bankIcon from 'utility-icons/bank.svg';
import institutionIcon from 'utility-icons/institution.svg';
import lightningIcon from 'utility-icons/lightning.svg';
import lockIcon from 'utility-icons/lock.svg';
import { toastSuccess } from 'utils';
import { setBankAccounts, setSelectedBankAccount } from '../../actions';
import { FlowActions } from '../../components';
import { BANK_ACCOUNT_PARAM, CHANGE_BANK_ACCOUNT_PARAM, PLAID_FALLBACK_PARAM, } from '../../constants';
import { useAuthorizationToken, useFlexFinalizeData, useFlowPath, } from '../../hooks';
import { getPostBankConnectionRoute, isChangingBankAccount } from '../../utils';
import { ConnectMethodOption } from '../components';
function ConnectBankAccount() {
    const dispatch = useDispatch();
    const [linkToken, setLinkToken] = useState(null);
    const [bankVerificationId, setBankVerificationId] = useState(null);
    const [linkTokenExpiresAt, setLinkTokenExpiresAt] = useState(null);
    const [shouldFetchToken, setShouldFetchToken] = useState(true);
    const [isPlaidUnavailable, setIsPlaidUnavailable] = useState(false);
    const [connectMethod, setConnectMethod] = useState('plaid');
    const history = useHistory();
    const location = useLocation();
    const isChangingAccount = isChangingBankAccount(location.search);
    const changeParams = isChangingAccount
        ? { [CHANGE_BANK_ACCOUNT_PARAM]: '1' }
        : undefined;
    const authorizationToken = useAuthorizationToken();
    const flowPath = useFlowPath(authorizationToken);
    const { lenderAccountIdentifier, finalizeData } = useFlexFinalizeData(authorizationToken);
    const manualEntryPath = (afterPlaidFailure = false) => flowPath('/autopay/add-bank-account', {
        ...(isChangingAccount ? { [CHANGE_BANK_ACCOUNT_PARAM]: '1' } : {}),
        ...(afterPlaidFailure ? { [PLAID_FALLBACK_PARAM]: '1' } : {}),
    });
    const linkedAccountPath = (bankAccountId) => flowPath(isChangingAccount ? '/autopay' : getPostBankConnectionRoute(finalizeData), bankAccountId
        ? { [BANK_ACCOUNT_PARAM]: String(bankAccountId) }
        : undefined);
    const [postBankVerification, { isLoading }] = usePostServicingV1AccountsLenderAccountIdentifierBankVerificationMutation();
    const [exchangePublicToken, { isLoading: isExchangingToken }] = usePutServicingV1AccountsLenderAccountIdentifierBankVerificationExchangePublicTokenMutation();
    const [postBankAccount] = usePostServicingV1AccountsLenderAccountIdentifierBankAccountsMutation();
    // Fetch a link token when needed
    useEffect(() => {
        if (lenderAccountIdentifier && authorizationToken && shouldFetchToken) {
            setShouldFetchToken(false);
            postBankVerification({ lenderAccountIdentifier }).then((result) => {
                if ('data' in result) {
                    const data = result.data;
                    setLinkToken(data.linkToken);
                    setBankVerificationId(data.bankVerificationId);
                    setLinkTokenExpiresAt(data.expiresAt);
                }
                else if ('error' in result) {
                    setIsPlaidUnavailable(true);
                }
            });
        }
    }, [
        lenderAccountIdentifier,
        authorizationToken,
        shouldFetchToken,
        postBankVerification,
    ]);
    const isTokenExpired = () => {
        if (!linkTokenExpiresAt)
            return true;
        const expiresAt = new Date(linkTokenExpiresAt);
        const now = new Date();
        // Consider expired 30 seconds early to avoid race conditions
        return now.getTime() >= expiresAt.getTime() - 30_000;
    };
    const refreshLinkToken = () => {
        setLinkToken(null);
        setBankVerificationId(null);
        setLinkTokenExpiresAt(null);
        setIsPlaidUnavailable(false);
        setShouldFetchToken(true);
    };
    const fallBackToManualEntry = () => history.push(manualEntryPath(true));
    const handleOpen = () => {
        if (isPlaidUnavailable) {
            fallBackToManualEntry();
        }
        else if (isTokenExpired()) {
            refreshLinkToken();
        }
        else {
            open();
        }
    };
    const handleContinue = () => {
        if (connectMethod === 'manual') {
            history.push(manualEntryPath());
            return;
        }
        handleOpen();
    };
    const { open, ready } = usePlaidLink({
        token: linkToken || '',
        onSuccess: (publicToken) => {
            // Exchange public token for access token
            if (lenderAccountIdentifier && bankVerificationId) {
                exchangePublicToken({
                    lenderAccountIdentifier,
                    putServicingV1AccountsLenderAccountIdentifierBankVerificationExchangePublicToken: { publicToken, bankVerificationId },
                }).then((result) => {
                    if ('data' in result) {
                        const { plaidBankAccounts } = result.data;
                        if (plaidBankAccounts && plaidBankAccounts.length > 1) {
                            // Multiple bank accounts
                            dispatch(setBankAccounts(plaidBankAccounts));
                            history.push(flowPath('/autopay-account-selection', changeParams));
                        }
                        else if (plaidBankAccounts && plaidBankAccounts.length === 1) {
                            // Single bank account
                            dispatch(setSelectedBankAccount(plaidBankAccounts[0]));
                            postBankAccount({
                                lenderAccountIdentifier,
                                postServicingV1AccountsLenderAccountIdentifierBankAccounts: {
                                    plaidBankAccountId: plaidBankAccounts[0].id,
                                },
                            })
                                .unwrap()
                                .then((created) => {
                                if (isChangingAccount)
                                    toastSuccess('Bank Account Updated');
                                history.push(linkedAccountPath(created.id));
                            })
                                .catch(() => {
                                fallBackToManualEntry();
                            });
                        }
                        else {
                            history.push(flowPath(isChangingAccount
                                ? '/autopay'
                                : getPostBankConnectionRoute(finalizeData)));
                        }
                    }
                    else if ('error' in result) {
                        fallBackToManualEntry();
                    }
                });
            }
        },
        onExit: () => {
            // User exited Plaid Link without completing — refresh the token
            // so the next attempt gets a fresh one
            refreshLinkToken();
        },
    });
    if (!lenderAccountIdentifier || !finalizeData) {
        return (_jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }));
    }
    const isPlaidPending = !ready || isLoading || isExchangingToken || !linkToken;
    const isContinueDisabled = connectMethod === 'plaid' && !isPlaidUnavailable ? isPlaidPending : false;
    return (_jsxs("div", { className: "connect-bank-account", children: [_jsx(H1, { variant: "headline-large", children: "Connect Bank Account" }), _jsxs("div", { className: "connect-bank-account__card", children: [_jsx("div", { className: "connect-bank-account__illustration", children: _jsx("img", { src: institutionIcon, alt: "" }) }), _jsxs("div", { className: "text-center", children: [_jsx(P, { variant: "body-large", children: "Alphaeon uses Plaid to securely retrieve your bank account information. Your credentials are never accessible or stored by Alphaeon." }), finalizeData.downPaymentRequired && (_jsx(P, { variant: "body-large", className: "mar-t-20", children: "The bank account that you connect will be used for your down payment (due today) as well as future payments." }))] }), _jsxs("div", { role: "radiogroup", "aria-label": "How to connect your bank account", children: [_jsx(ConnectMethodOption, { value: "plaid", checked: connectMethod === 'plaid', onChange: setConnectMethod, icon: lightningIcon, iconTone: "orange", title: "Connect Instantly", description: "Log into your bank through Plaid. Verified in seconds with best-in-class encryption.", badge: "Recommended" }), _jsx("div", { className: "connect-method-divider", children: _jsx(Span, { variant: "body-small", bold: true, children: "OR" }) }), _jsx(ConnectMethodOption, { value: "manual", checked: connectMethod === 'manual', onChange: setConnectMethod, icon: bankIcon, iconTone: "grey", title: "Enter Account Manually", description: "Provide your routing and account number." })] }), _jsxs("div", { className: "connect-bank-account__assurance", children: [_jsx("img", { src: lockIcon, alt: "" }), _jsx(Span, { variant: "body-small", children: "Bank-grade encryption. We never store your login." })] })] }), _jsx(FlowActions, { secondary: _jsx(ButtonV2, { type: "button", variant: "ghost", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => history.push(flowPath(isChangingAccount ? '/autopay' : '/next-steps')), children: "Back" }), children: _jsx(ButtonV2, { variant: "filled", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: handleContinue, disabled: isContinueDisabled, children: connectMethod === 'plaid' && isLoading
                        ? 'Loading...'
                        : connectMethod === 'plaid' && isExchangingToken
                            ? 'Connecting...'
                            : 'Connect Bank Account' }) })] }));
}
export default ConnectBankAccount;
